import { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Search, X, Plus, MapPin, Users, Building2, Calendar,
  CalendarCheck, ChevronDown, Edit, Trash2, Check, Loader2,
  ExternalLink, Mail, Phone, Globe, Scissors, Activity, Stethoscope, Heart,
  Instagram, Facebook, Music2, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ==================== CUSTOM MARKERS ====================

const createIcon = (color, label, size = 32) => {
  return L.divIcon({
    className: "odr-marker",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:${size < 30 ? 9 : 11}px;font-weight:700;
      font-family:Arial,sans-serif;letter-spacing:0.5px;
    ">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2 + 4)],
  });
};

const ICONS = {
  client: createIcon("#e91e63", "CE", 30),
  hairdresser: createIcon("#9c27b0", "PA", 30),
  agent: createIcon("#2d7a5a", "AG", 30),
  distributor: createIcon("#c9a96e", "DS", 30),
  physiotherapist: createIcon("#1976d2", "FT", 30),
  hospital: createIcon("#d32f2f", "OS", 30),
  association: createIcon("#ff9800", "AS", 30),
  upcoming: createIcon("#d94f4f", "EV", 36),
  past: createIcon("#999", "EV", 28),
};

const LAYER_CONFIG = {
  client: { label: "Centri Estetici", color: "#e91e63", icon: Building2 },
  hairdresser: { label: "Parrucchieri", color: "#9c27b0", icon: Scissors },
  agent: { label: "Agenti", color: "#2d7a5a", icon: Users },
  distributor: { label: "Distributori", color: "#c9a96e", icon: Globe },
  physiotherapist: { label: "Fisioterapisti", color: "#1976d2", icon: Activity },
  hospital: { label: "Ospedali", color: "#d32f2f", icon: Stethoscope },
  association: { label: "Associazioni", color: "#ff9800", icon: Heart },
  upcoming: { label: "Eventi Futuri", color: "#d94f4f", icon: CalendarCheck },
  past: { label: "Eventi Passati", color: "#999", icon: Calendar },
};

// ==================== FIT BOUNDS HELPER ====================
const FitBounds = ({ points, events }) => {
  const map = useMap();
  useEffect(() => {
    const allCoords = [
      ...points.filter(p => p.lat && p.lng).map(p => [p.lat, p.lng]),
      ...events.filter(e => e.lat && e.lng).map(e => [e.lat, e.lng]),
    ];
    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, events, map]);
  return null;
};

// ==================== SYNC ACCADEMIA BUTTON ====================
const SyncAccademiaButton = ({ onDone }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const dryRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/odr-network/points/sync-accademia?dry_run=true`, { method: "POST" });
      const d = await r.json();
      setPreview(d);
    } catch (e) {
      alert("Errore: " + e.message);
    }
    setLoading(false);
  };

  const execute = async () => {
    if (!window.confirm(`Inviare la mail Accademia a ${preview.candidates} clienti?\nGli account esistenti verranno riusati (no doppi).`)) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/odr-network/points/sync-accademia`, { method: "POST" });
      const d = await r.json();
      setResult(d);
      setPreview(null);
      onDone && onDone();
    } catch (e) {
      alert("Errore: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-xl p-3 space-y-2" data-testid="sync-accademia-box">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs font-bold">A</span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-stone-800">Sincronizza con Accademia</div>
          <div className="text-[11px] text-stone-500">Crea account video-corsi per i clienti del Global</div>
        </div>
      </div>
      {!preview && !result && (
        <Button size="sm" className="w-full rounded-full bg-rose-500 hover:bg-rose-600 text-xs" onClick={dryRun} disabled={loading} data-testid="sync-accademia-check">
          {loading ? "Controllo..." : "Controlla candidati"}
        </Button>
      )}
      {preview && (
        <div className="space-y-2">
          <div className="text-xs text-stone-700">
            <strong>{preview.candidates}</strong> clienti senza account Accademia.
            {preview.candidates > 0 && (
              <ul className="mt-1 max-h-32 overflow-y-auto text-[10px] text-stone-500 list-disc list-inside">
                {preview.preview.slice(0, 8).map((p, i) => <li key={i}>{p.name} - {p.email}</li>)}
                {preview.candidates > 8 && <li>...e altri {preview.candidates - 8}</li>}
              </ul>
            )}
          </div>
          {preview.candidates > 0 ? (
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-xs" onClick={execute} disabled={loading} data-testid="sync-accademia-execute">
                {loading ? "Invio..." : `Invia mail a ${preview.candidates}`}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => setPreview(null)}>Annulla</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="w-full rounded-full text-xs" onClick={() => setPreview(null)}>Tutto sincronizzato ✓</Button>
          )}
        </div>
      )}
      {result && (
        <div className="text-xs text-stone-700 space-y-1 bg-white rounded-lg p-2" data-testid="sync-accademia-result">
          <div>✓ Processati: <strong>{result.processed}</strong></div>
          <div className="text-emerald-700">✓ Nuovi: {result.created} · Riusati: {result.reused}</div>
          <div className="text-stone-500">Mail inviate: {result.email_sent}</div>
          {result.errors.length > 0 && <div className="text-red-600">Errori: {result.errors.length}</div>}
          <Button size="sm" variant="ghost" className="w-full text-xs h-7" onClick={() => setResult(null)}>Chiudi</Button>
        </div>
      )}

      {/* Repair: per chi ha già fatto il sync ma non vede i lead in Approvati */}
      <RepairLeadsButton />
    </div>
  );
};

const RepairLeadsButton = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const repair = async () => {
    if (!window.confirm("Vuoi ripristinare i lead 'Approvati' per i clienti del Global che hanno già l'account ma non risultano nella tab Approvati? NON verranno inviate nuove mail.")) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/odr-network/points/repair-leads`, { method: "POST" });
      setResult(await r.json());
    } catch (e) {
      alert("Errore: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="mt-2 pt-2 border-t border-rose-200">
      {!result ? (
        <Button size="sm" variant="ghost" className="w-full text-[10px] text-stone-600 hover:text-rose-600 h-7" onClick={repair} disabled={loading} data-testid="repair-leads-btn">
          {loading ? "Ripristino..." : "↻ Ripristina lead 'Approvati' mancanti"}
        </Button>
      ) : (
        <div className="text-[11px] text-stone-700 bg-white rounded-lg p-2 space-y-0.5">
          <div>Clienti scansionati: <strong>{result.candidates}</strong></div>
          <div className="text-emerald-700">Aggiunti a 'Approvati': {result.created_or_updated}</div>
          <div className="text-stone-500">Già presenti: {result.skipped}</div>
          <Button size="sm" variant="ghost" className="w-full text-[10px] h-6" onClick={() => setResult(null)}>Chiudi</Button>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
// ==================== PUBLIC MAP VISIBILITY (Admin toggle) ====================
const PublicMapVisibility = () => {
  const [enabled, setEnabled] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/odr-network/public/settings`).then((res) => {
      setEnabled(res.data?.enabled_layers || []);
      setAllowed(res.data?.allowed_layers || []);
    }).finally(() => setLoading(false));
  }, []);

  const toggle = async (layer) => {
    const next = enabled.includes(layer) ? enabled.filter((l) => l !== layer) : [...enabled, layer];
    setEnabled(next);
    setSaving(true);
    try {
      await axios.put(`${API}/odr-network/public/settings`, { enabled_layers: next });
      toast.success("Visibilità mappa pubblica aggiornata");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="rounded-xl border-[#7b5ea7]/30 bg-purple-50/30" data-testid="public-map-visibility">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#2d1b4e] flex items-center gap-2">
          🌐 Visibilità Mappa Pubblica
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        </CardTitle>
        <p className="text-[11px] text-gray-500 leading-tight mt-1">
          Scegli quali pulsanti-filtro appaiono nella mappa pubblica embed sul sito ODR (odr.ioxina.com).
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {allowed.map((layer) => {
          const cfg = LAYER_CONFIG[layer];
          if (!cfg) return null;
          const Icon = cfg.icon;
          const isEnabled = enabled.includes(layer);
          return (
            <label
              key={layer}
              className="flex items-center gap-2 cursor-pointer text-xs hover:bg-white p-1.5 rounded-lg transition-colors"
              data-testid={`public-vis-toggle-${layer}`}
            >
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => toggle(layer)}
                disabled={saving}
                className="w-4 h-4 cursor-pointer accent-[#7b5ea7]"
              />
              <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
              <span className={`flex-1 ${isEnabled ? "font-semibold text-gray-800" : "text-gray-400"}`}>
                {cfg.label}
              </span>
              {isEnabled && <Check className="w-3 h-3 text-green-600" />}
            </label>
          );
        })}
      </CardContent>
    </Card>
  );
};


const OdrNetworkMapPage = () => {
  const [mapData, setMapData] = useState({ points: [], events: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLayers, setActiveLayers] = useState(["client", "agent", "distributor", "hairdresser", "physiotherapist", "hospital", "association", "upcoming"]);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [showAdmin, setShowAdmin] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState(null);
  const [filterRegion, setFilterRegion] = useState("");
  const [filterProvincia, setFilterProvincia] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [allRegions, setAllRegions] = useState([]);
  const [allProvince, setAllProvince] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const searchTimeout = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const layers = activeLayers.join(",");
      const params = { layers };
      if (searchDebounced) params.search = searchDebounced;
      const [dataRes, statsRes] = await Promise.all([
        axios.get(`${API}/odr-network/map-data`, { params }),
        axios.get(`${API}/odr-network/stats`),
      ]);
      setMapData(dataRes.data);
      setStats(statsRes.data);
      // Build full filter lists from ALL points (not filtered)
      const pts = dataRes.data.points || [];
      setAllRegions([...new Set(pts.map(p => p.region).filter(Boolean))].sort());
      setAllProvince([...new Set(pts.map(p => p.provincia).filter(Boolean))].sort());
      setAllCities([...new Set(pts.map(p => p.city).filter(Boolean))].sort());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeLayers, searchDebounced]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchDebounced(val), 400);
  };

  const toggleLayer = (layer) => {
    setActiveLayers((prev) =>
      prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer]
    );
  };

  const savePoint = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/odr-network/points/${editItem}`, editForm);
        toast.success("Punto aggiornato");
      } else {
        await axios.post(`${API}/odr-network/points`, editForm);
        toast.success("Punto creato");
      }
      setEditItem(null);
      setEditForm({});
      setAddType(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const saveEvent = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/odr-network/events/${editItem}`, editForm);
        toast.success("Evento aggiornato");
      } else {
        await axios.post(`${API}/odr-network/events`, editForm);
        toast.success("Evento creato");
      }
      setEditItem(null);
      setEditForm({});
      setAddType(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const deletePoint = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try {
      await axios.delete(`${API}/odr-network/points/${id}`);
      toast.success("Eliminato");
      loadData();
    } catch (e) { toast.error("Errore"); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try {
      await axios.delete(`${API}/odr-network/events/${id}`);
      toast.success("Eliminato");
      loadData();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]" data-testid="odr-network-map">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2d1b4e] to-[#5a3d7a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <img
              src="/odr-assets/odr_logo_circle.jpeg"
              alt="ODR"
              className="w-12 h-12 rounded-full border-2 border-[#c9a96e] shadow-lg"
            />
            <div>
              <h1 className="text-xl font-bold tracking-wide" data-testid="map-title">
                ODR Global Network
              </h1>
              <p className="text-xs text-[#d4c0e8] tracking-wider uppercase">
                Centri · Parrucchieri · Agenti · Distributori · Fisio · Ospedali · Ass.
              </p>
            </div>
          </div>
          {stats && (
            <div className="flex gap-3 text-center flex-wrap">
              {[
                { n: stats.clients, l: "Centri", c: "#e91e63", key: "client" },
                { n: stats.hairdressers, l: "Parrucch.", c: "#9c27b0", key: "hairdresser" },
                { n: stats.agents, l: "Agenti", c: "#2d7a5a", key: "agent" },
                { n: stats.distributors, l: "Distrib.", c: "#c9a96e", key: "distributor" },
                { n: stats.physiotherapists, l: "Fisio", c: "#1976d2", key: "physiotherapist" },
                { n: stats.hospitals, l: "Ospedali", c: "#d32f2f", key: "hospital" },
                { n: stats.associations, l: "Ass.", c: "#ff9800", key: "association" },
                { n: stats.upcoming_events, l: "Eventi", c: "#d94f4f", key: "upcoming" },
              ].map((s) => {
                const active = activeLayers.includes(s.key);
                return (
                  <button
                    key={s.l}
                    onClick={() => toggleLayer(s.key)}
                    data-testid={`stat-toggle-${s.key}`}
                    className={`px-2 py-1 rounded-lg transition-all ${active ? "bg-white/10" : "opacity-40 hover:opacity-70"}`}
                    title={`${active ? "Nascondi" : "Mostra"} ${s.l} sulla mappa`}
                  >
                    <p className="text-lg font-bold leading-none" style={{ color: s.c }}>{s.n ?? 0}</p>
                    <p className="text-[9px] text-[#d4c0e8] mt-0.5">{s.l}</p>
                  </button>
                );
              })}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-white"
            onClick={() => setShowAdmin(!showAdmin)}
            data-testid="toggle-admin-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Gestisci
          </Button>
        </div>
      </div>

      {/* Filter Bar - Layer toggles */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-[1000]">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
          {Object.entries(LAYER_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = activeLayers.includes(key);
            return (
              <button
                key={key}
                onClick={() => toggleLayer(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  active
                    ? "text-white shadow-md"
                    : "bg-white text-gray-400 border-gray-200 hover:border-gray-400"
                }`}
                style={active ? { backgroundColor: cfg.color, borderColor: cfg.color } : {}}
                data-testid={`filter-${key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
              </button>
            );
          })}
          <div className="ml-auto">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Cerca citta, nome..."
                className="pl-8 h-8 text-xs rounded-full w-[180px]"
                data-testid="map-search"
              />
              {search && (
                <button onClick={() => { setSearch(""); setSearchDebounced(""); }} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown filters row - Regione, Provincia, Citta */}
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 border-t border-gray-100 bg-gray-50/50">
          <MapPin className="w-4 h-4 text-[#7b5ea7]" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Filtra per:</span>
          {/* Regione */}
          <select
            value={filterRegion}
            onChange={(e) => { setFilterRegion(e.target.value); setFilterProvincia(""); setFilterCity(""); }}
            className="h-9 text-sm rounded-lg border-2 border-gray-200 px-3 bg-white text-gray-700 font-medium focus:outline-none focus:border-[#7b5ea7] focus:ring-2 focus:ring-[#7b5ea7]/20 min-w-[170px]"
            data-testid="filter-region"
          >
            <option value="">Tutte le Regioni</option>
            {allRegions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          {/* Provincia */}
          <select
            value={filterProvincia}
            onChange={(e) => { setFilterProvincia(e.target.value); setFilterCity(""); }}
            className="h-9 text-sm rounded-lg border-2 border-gray-200 px-3 bg-white text-gray-700 font-medium focus:outline-none focus:border-[#7b5ea7] focus:ring-2 focus:ring-[#7b5ea7]/20 min-w-[170px]"
            data-testid="filter-provincia"
          >
            <option value="">Tutte le Province</option>
            {[...new Set(
              (mapData.points || [])
                .filter((p) => !filterRegion || p.region === filterRegion)
                .map((p) => p.provincia)
                .filter(Boolean)
            )].sort().map((pv) => (
              <option key={pv} value={pv}>{pv}</option>
            ))}
          </select>
          {/* Citta */}
          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="h-9 text-sm rounded-lg border-2 border-gray-200 px-3 bg-white text-gray-700 font-medium focus:outline-none focus:border-[#7b5ea7] focus:ring-2 focus:ring-[#7b5ea7]/20 min-w-[180px]"
            data-testid="filter-city"
          >
            <option value="">Tutte le Citta</option>
            {[...new Set(
              (mapData.points || [])
                .filter((p) => (!filterRegion || p.region === filterRegion) && (!filterProvincia || p.provincia === filterProvincia))
                .map((p) => p.city)
                .filter(Boolean)
            )].sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {(filterRegion || filterProvincia || filterCity) && (
            <button
              onClick={() => { setFilterRegion(""); setFilterProvincia(""); setFilterCity(""); }}
              className="h-9 px-4 text-xs font-semibold text-white bg-[#7b5ea7] hover:bg-[#6b4e97] rounded-lg transition-colors"
              data-testid="reset-filters-btn"
            >
              Resetta filtri
            </button>
          )}
          {(filterRegion || filterProvincia || filterCity) && (
            <span className="text-xs text-gray-400 ml-2">
              {mapData.points.filter((p) => {
                if (filterRegion && p.region !== filterRegion) return false;
                if (filterProvincia && p.provincia !== filterProvincia) return false;
                if (filterCity && p.city !== filterCity) return false;
                return true;
              }).length} risultati
            </span>
          )}
        </div>
      </div>

      <div className="flex" style={{ height: "calc(100vh - 180px)" }}>
        {/* MAP */}
        <div className={`flex-1 relative ${showAdmin ? "w-2/3" : "w-full"}`} data-testid="map-container">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[#7b5ea7]" />
            </div>
          ) : (
            <MapContainer
              center={[44.4, 11.3]}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              zoomControl={true}
            >
              {/* Base layer - OpenStreetMap with visible borders */}
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {/* Administrative boundaries overlay */}
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
                opacity={0.7}
              />
              <FitBounds points={mapData.points} events={mapData.events} />

              {/* Points - filtered by dropdown */}
              {mapData.points
                .filter((p) => {
                  if (filterRegion && p.region !== filterRegion) return false;
                  if (filterProvincia && p.provincia !== filterProvincia) return false;
                  if (filterCity && p.city !== filterCity) return false;
                  return true;
                })
                .map((p) => (
                <Marker key={p.point_id} position={[p.lat, p.lng]} icon={ICONS[p.type] || ICONS.client}>
                  <Popup maxWidth={280}>
                    <div className="text-sm">
                      {/* Logo grande in alto */}
                      {p.logo_url && (
                        <div className="bg-white rounded p-2 mb-2 border border-gray-100 flex items-center justify-center" style={{ minHeight: 60 }}>
                          <img src={p.logo_url} alt="" style={{ maxHeight: 70, maxWidth: "100%", objectFit: "contain" }} />
                        </div>
                      )}
                      <Badge style={{ backgroundColor: LAYER_CONFIG[p.type]?.color, color: "#fff", fontSize: "10px" }}>
                        {LAYER_CONFIG[p.type]?.label || p.type}
                      </Badge>
                      {p.accademia_user_id && (
                        <Badge
                          className="ml-1"
                          style={{
                            backgroundColor: "#7b5ea7",
                            color: "#fff",
                            fontSize: "10px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          title="Account Accademia attivato"
                          data-testid={`badge-accademia-${p.point_id}`}
                        >
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 14, height: 14, borderRadius: "50%",
                            background: "#fff", color: "#7b5ea7",
                            fontSize: 9, fontWeight: 700,
                          }}>A</span>
                          Videocorsi Academy
                        </Badge>
                      )}
                      <p className="font-bold text-sm mb-1 mt-2" style={{ color: "#2d1b4e" }}>{p.name}</p>
                      {p.contact_person && <p className="text-xs text-gray-600">{p.contact_person}</p>}
                      {p.address && <p className="text-xs text-gray-600"><MapPin className="w-3 h-3 inline mr-1" />{p.address}</p>}
                      {p.city && <p className="text-xs text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />{p.city}{p.country ? `, ${p.country}` : ""}</p>}
                      {p.email && <p className="text-xs mt-1"><Mail className="w-3 h-3 inline mr-1" /><a href={`mailto:${p.email}`} className="text-[#7b5ea7]">{p.email}</a></p>}
                      {p.phone && <p className="text-xs"><Phone className="w-3 h-3 inline mr-1" />{p.phone}</p>}
                      {p.description && <p className="text-xs text-gray-500 mt-1">{p.description}</p>}
                      {/* Social icons (always visible if has_* OR url present) */}
                      {(() => {
                        const items = [
                          { has: !!(p.website), url: p.website, color: "#666", Icon: Globe, title: "Sito web", normalize: (u) => u.startsWith("http") ? u : `https://${u}` },
                          { has: !!(p.has_instagram || p.instagram), url: p.instagram, color: "#E1306C", Icon: Instagram, title: "Instagram", normalize: (u) => u.startsWith("http") ? u : `https://instagram.com/${u.replace("@", "")}` },
                          { has: !!(p.has_facebook || p.facebook), url: p.facebook, color: "#1877F2", Icon: Facebook, title: "Facebook", normalize: (u) => u.startsWith("http") ? u : `https://facebook.com/${u}` },
                          { has: !!(p.has_tiktok || p.tiktok), url: p.tiktok, color: "#000", Icon: Music2, title: "TikTok", normalize: (u) => u.startsWith("http") ? u : `https://tiktok.com/@${u.replace("@", "")}` },
                          { has: !!(p.has_whatsapp || p.whatsapp), url: p.whatsapp, color: "#25D366", Icon: MessageCircle, title: "WhatsApp", normalize: (u) => `https://wa.me/${u.replace(/\D/g, "")}` },
                        ].filter(i => i.has);
                        if (!items.length) return null;
                        return (
                          <div className="flex gap-2 mt-2 items-center flex-wrap">
                            {items.map((it, idx) => {
                              const hasUrl = !!it.url && it.url.trim().length > 0;
                              const href = hasUrl ? it.normalize(it.url) : null;
                              const common = { title: hasUrl ? it.title : `${it.title} (link da inserire)` };
                              if (hasUrl) {
                                return (
                                  <a key={idx} href={href} target="_blank" rel="noopener noreferrer" style={{ color: it.color }} className="hover:opacity-70" {...common}>
                                    <it.Icon className="w-4 h-4" />
                                  </a>
                                );
                              }
                              return (
                                <span key={idx} style={{ color: "#bbb" }} className="cursor-not-allowed" {...common}>
                                  <it.Icon className="w-4 h-4" />
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Events */}
              {mapData.events.map((e) => (
                <Marker key={e.event_id} position={[e.lat, e.lng]} icon={ICONS[e.status] || ICONS.upcoming}>
                  <Popup maxWidth={300}>
                    <div className="text-sm">
                      <Badge style={{
                        backgroundColor: e.status === "upcoming" ? "#d94f4f" : "#999",
                        color: "#fff", fontSize: "10px",
                      }}>
                        {e.status === "upcoming" ? "Prossimo" : "Passato"} &middot; {e.event_type}
                      </Badge>
                      {e.image_url && (
                        <img src={e.image_url} alt={e.title} className="w-full h-24 object-cover rounded mt-2" />
                      )}
                      <p className="font-bold text-sm mt-2" style={{ color: "#2d1b4e" }}>{e.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {e.date_start}{e.date_end ? ` — ${e.date_end}` : ""}
                      </p>
                      {e.city && <p className="text-xs text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />{e.city}{e.country ? `, ${e.country}` : ""}</p>}
                      {e.description && <p className="text-xs text-gray-500 mt-1">{e.description}</p>}
                      {e.status === "upcoming" && (
                        <Button size="sm" className="mt-2 rounded-full bg-[#d94f4f] hover:bg-[#c03030] text-xs w-full">
                          {e.cta_label || "Richiedi Info"}
                        </Button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          {/* Legend overlay */}
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg z-[999] text-xs" data-testid="map-legend">
            <p className="font-semibold mb-1.5 text-[#2d1b4e]">Legenda</p>
            {Object.entries(LAYER_CONFIG).map(([key, cfg]) => (
              activeLayers.includes(key) && (
                <div key={key} className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cfg.color }} />
                  <span className="text-gray-600">{cfg.label}</span>
                </div>
              )
            ))}
            <p className="text-[10px] text-gray-400 mt-1.5 border-t pt-1">
              {mapData.points?.length || 0} punti &middot; {mapData.events?.length || 0} eventi
            </p>
          </div>
        </div>

        {/* ADMIN PANEL (side) */}
        {showAdmin && (
          <div className="w-1/3 bg-white border-l overflow-y-auto p-4 space-y-4 z-[999]" data-testid="admin-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-[#2d1b4e]">Gestione Network</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAdmin(false)}><X className="w-4 h-4" /></Button>
            </div>

            {/* Sync Accademia */}
            <SyncAccademiaButton onDone={loadData} />

            {/* Toggle visibilità mappa pubblica */}
            <PublicMapVisibility />

            {/* Add buttons */}
            <div className="flex flex-wrap gap-2">
              {["client", "hairdresser", "agent", "distributor", "physiotherapist", "hospital", "association"].map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant="outline"
                  className="rounded-full text-xs"
                  style={{ borderColor: LAYER_CONFIG[t].color, color: LAYER_CONFIG[t].color }}
                  onClick={() => { setAddType(t); setEditItem(null); setEditForm({ type: t }); }}
                  data-testid={`add-${t}-btn`}
                >
                  <Plus className="w-3 h-3 mr-1" /> {LAYER_CONFIG[t].label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-xs border-[#d94f4f] text-[#d94f4f]"
                onClick={() => { setAddType("event"); setEditItem(null); setEditForm({ status: "upcoming", event_type: "course" }); }}
                data-testid="add-event-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Evento
              </Button>
            </div>

            {/* Add/Edit Form */}
            {(addType || editItem) && (
              <Card key={editItem || `new-${addType}`} className="rounded-xl border-[#c9a96e]/30" data-testid="edit-form">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{editItem ? "Modifica" : "Aggiungi"} {addType === "event" ? "Evento" : "Punto"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {addType === "event" ? (
                    <>
                      <Input placeholder="Titolo *" value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="text-xs h-8" />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={editForm.event_type || "course"} onChange={(e) => setEditForm({ ...editForm, event_type: e.target.value })} className="text-xs h-8 border rounded px-2">
                          <option value="course">Corso</option>
                          <option value="training">Formazione</option>
                          <option value="workshop">Workshop</option>
                          <option value="conference">Conferenza</option>
                        </select>
                        <select value={editForm.status || "upcoming"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="text-xs h-8 border rounded px-2">
                          <option value="upcoming">Prossimo</option>
                          <option value="past">Passato</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="date" placeholder="Data inizio" value={editForm.date_start || ""} onChange={(e) => setEditForm({ ...editForm, date_start: e.target.value })} className="text-xs h-8" />
                        <Input type="date" placeholder="Data fine" value={editForm.date_end || ""} onChange={(e) => setEditForm({ ...editForm, date_end: e.target.value })} className="text-xs h-8" />
                      </div>
                      <Input placeholder="Indirizzo" value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="text-xs h-8" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Citta" value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="text-xs h-8" />
                        <Input placeholder="Paese" value={editForm.country || ""} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="text-xs h-8" />
                      </div>
                      <Input placeholder="Descrizione" value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="text-xs h-8" />
                      <Input placeholder="URL Immagine" value={editForm.image_url || ""} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })} className="text-xs h-8" />
                      <Input placeholder="Testo CTA" value={editForm.cta_label || "Richiedi Info"} onChange={(e) => setEditForm({ ...editForm, cta_label: e.target.value })} className="text-xs h-8" />
                    </>
                  ) : (
                    <>
                      <Input placeholder="Nome *" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="text-xs h-8" />
                      <select value={editForm.type || "client"} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} className="text-xs h-8 border rounded px-2 w-full">
                        <option value="client">Centro Estetico</option>
                        <option value="hairdresser">Parrucchiere</option>
                        <option value="agent">Agente</option>
                        <option value="distributor">Distributore</option>
                        <option value="physiotherapist">Fisioterapista</option>
                        <option value="hospital">Ospedale</option>
                        <option value="association">Associazione</option>
                      </select>
                      <Input placeholder="Indirizzo" value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="text-xs h-8" />
                      <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="Citta" value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="text-xs h-8" />
                        <Input placeholder="Prov." value={editForm.provincia || ""} onChange={(e) => setEditForm({ ...editForm, provincia: e.target.value.toUpperCase() })} className="text-xs h-8" maxLength={4} />
                        <Input placeholder="CAP" value={editForm.cap || ""} onChange={(e) => setEditForm({ ...editForm, cap: e.target.value })} className="text-xs h-8" maxLength={5} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Regione" value={editForm.region || ""} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })} className="text-xs h-8" />
                        <Input placeholder="Paese (es. Bulgaria, Italia...)" value={editForm.country || ""} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="text-xs h-8" />
                      </div>
                      <Input placeholder="Referente" value={editForm.contact_person || ""} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} className="text-xs h-8" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="text-xs h-8" />
                        <Input placeholder="Telefono" value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="text-xs h-8" />
                      </div>
                      <Input placeholder="Sito web" value={editForm.website || ""} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} className="text-xs h-8" />
                      <div className="border rounded-lg p-2 space-y-2 bg-gray-50/50">
                        <p className="text-[10px] text-gray-500 font-semibold uppercase">Social</p>
                        {[
                          { key: "instagram", has: "has_instagram", label: "Instagram", placeholder: "@user o URL" },
                          { key: "facebook", has: "has_facebook", label: "Facebook", placeholder: "URL o pagina" },
                          { key: "tiktok", has: "has_tiktok", label: "TikTok", placeholder: "@user o URL" },
                          { key: "whatsapp", has: "has_whatsapp", label: "WhatsApp", placeholder: "+39 333..." },
                        ].map((sf) => (
                          <div key={sf.key} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!editForm[sf.has]}
                              onChange={(e) => setEditForm({ ...editForm, [sf.has]: e.target.checked })}
                              className="w-3.5 h-3.5"
                              title={`${sf.label} presente (anche senza link)`}
                              data-testid={`has-${sf.key}`}
                            />
                            <span className="text-[11px] text-gray-600 w-16 shrink-0">{sf.label}</span>
                            <Input
                              placeholder={sf.placeholder}
                              value={editForm[sf.key] || ""}
                              onChange={(e) => setEditForm({ ...editForm, [sf.key]: e.target.value, [sf.has]: e.target.value ? true : editForm[sf.has] })}
                              className="text-xs h-7 flex-1"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] text-gray-500 font-medium">Logo (URL immagine)</label>
                        <div className="flex gap-1">
                          <Input placeholder="https://..." value={editForm.logo_url || ""} onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })} className="text-xs h-8 flex-1" />
                          {editForm.logo_url && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-red-500 border-red-200 hover:bg-red-50"
                              onClick={() => setEditForm({ ...editForm, logo_url: "" })}
                              data-testid="clear-logo-btn"
                              title="Rimuovi logo"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        {editForm.logo_url && (
                          <img src={editForm.logo_url} alt="logo preview" className="h-10 object-contain mt-1 border rounded p-1 bg-white" onError={(ev) => { ev.target.style.display = 'none'; }} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] text-gray-500 font-medium">Oppure carica file logo</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(ev) => {
                            const file = ev.target.files?.[0];
                            if (!file) return;
                            if (file.size > 500000) { toast.error("Immagine troppo grande (max 500KB)"); return; }
                            const reader = new FileReader();
                            reader.onload = () => setEditForm({ ...editForm, logo_url: reader.result });
                            reader.readAsDataURL(file);
                          }}
                          className="text-xs w-full"
                        />
                      </div>
                      <Input placeholder="Descrizione" value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="text-xs h-8" />
                    </>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="rounded-full bg-[#2d1b4e] text-xs" onClick={addType === "event" ? saveEvent : savePoint} disabled={saving}>
                      {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                      Salva
                    </Button>
                    {editItem && addType !== "event" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="btn-refresh-coords"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            const r = await fetch(`${API}/odr-network/points/refresh-coords?point_id=${editItem}`, { method: "POST" });
                            const d = await r.json();
                            if (d.updated > 0) {
                              const ch = d.changes_preview?.[0];
                              alert(`✅ Posizione aggiornata!\nVecchia: ${ch?.old?.[0]?.toFixed(4)}, ${ch?.old?.[1]?.toFixed(4)}\nNuova: ${ch?.new?.[0]?.toFixed(4)}, ${ch?.new?.[1]?.toFixed(4)}`);
                              loadData();
                            } else {
                              alert("Nessuna modifica: la posizione è già corretta oppure il geocoding non ha trovato un risultato più preciso.");
                            }
                          } catch (e) {
                            alert("Errore: " + e.message);
                          } finally {
                            setSaving(false);
                          }
                        }}
                        title="Ricalcola lat/lng dall'indirizzo (usa Nominatim + pgeocode)"
                      >
                        📍 Ricalcola posizione
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { setAddType(null); setEditItem(null); setEditForm({}); }}>
                      Annulla
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Points list */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Punti ({mapData.points?.length || 0})</p>
              {mapData.points?.slice(0, 30).map((p) => (
                <div key={p.point_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-xs group">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: LAYER_CONFIG[p.type]?.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    <p className="text-gray-400 truncate">{p.city}{p.country ? `, ${p.country}` : ""}</p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                    <button onClick={() => { setEditItem(p.point_id); setAddType(p.type); setEditForm(p); }} className="text-[#7b5ea7]"><Edit className="w-3 h-3" /></button>
                    <button onClick={() => deletePoint(p.point_id)} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Events list */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Eventi ({mapData.events?.length || 0})</p>
              {mapData.events?.map((e) => (
                <div key={e.event_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-xs group">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.status === "upcoming" ? "#d94f4f" : "#999" }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{e.title}</p>
                    <p className="text-gray-400">{e.date_start} &middot; {e.city}</p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                    <button onClick={() => { setEditItem(e.event_id); setAddType("event"); setEditForm(e); }} className="text-[#7b5ea7]"><Edit className="w-3 h-3" /></button>
                    <button onClick={() => deleteEvent(e.event_id)} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OdrNetworkMapPage;
